import moment from "moment-timezone";

export function mergeIntervals(intervals) {
  return intervals.filter(([start, end]) => end > start).sort((a, b) => a[0] - b[0]).reduce((merged, interval) => {
    const last = merged[merged.length - 1];
    if (last && interval[0] <= last[1]) last[1] = Math.max(last[1], interval[1]);
    else merged.push([...interval]);
    return merged;
  }, []);
}

export const intervalMinutes = (intervals) => intervals.reduce((total, [start, end]) => total + (end - start) / 60000, 0);

// Cut appointments and blocked time out of working hours.
// An empty busy list leaves the whole window, which is a fully open day.
export function subtractIntervals(windows, busy = []) {
  const busyRanges = mergeIntervals(busy);
  const openRanges = [];

  mergeIntervals(windows).forEach(([start, end]) => {
    let cursor = start;
    busyRanges.forEach(([busyStart, busyEnd]) => {
      const overlaps = busyEnd > cursor && busyStart < end;
      if (!overlaps) return;
      if (busyStart > cursor) openRanges.push([cursor, busyStart]);
      cursor = Math.max(cursor, busyEnd);
    });
    if (cursor < end) openRanges.push([cursor, end]);
  });

  return openRanges;
}

const releasedAppointment = new Set(["cancelled", "entered-in-error"]);

function belongsToPractitioner(item, practitionerRef, scheduleIds) {
  if (!practitionerRef) return true;
  if (item.practitionerRef === practitionerRef) return true;
  if ((item.practitionerRefs || []).includes(practitionerRef)) return true;
  const ids = [item.scheduleId, ...(item.scheduleIds || [])];
  return ids.some((id) => scheduleIds.has(id));
}

function toRange(item) {
  return [+new Date(item.startsAt), +new Date(item.endsAt)];
}

// Bookable time for one practitioner: working hours, minus blocks and appointments that still hold the slot.
export function availableWindows({ availability = [], blocks = [], appointments = [], practitionerRef, scheduleIds = [] }) {
  const ownedSchedules = new Set(scheduleIds);
  const isOwned = (item) => belongsToPractitioner(item, practitionerRef, ownedSchedules);
  const workingHours = availability.filter(isOwned).map(toRange);
  const stillBooked = appointments.filter((appointment) => !releasedAppointment.has(appointment.status) && isOwned(appointment));
  const busy = [...blocks.filter(isOwned), ...stillBooked].map(toRange);
  return subtractIntervals(workingHours, busy);
}

// Week summaries sometimes leave availableHours empty on a day with nothing booked.
// Show that day's working hours instead. A day that already has bookings keeps the reported gaps.
export function resolvedAvailable(summary = {}) {
  const reported = summary.availableHours || [];
  if (reported.length) {
    return { ranges: reported, minutes: summary.availableMinutes || intervalMinutes(reported) };
  }

  const hours = summary.hours || [];
  const nothingBooked = !(summary.appointmentCount > 0) && !(summary.blockMinutes > 0);
  if (nothingBooked && hours.length) {
    return { ranges: hours, minutes: summary.workMinutes || intervalMinutes(hours) };
  }

  return { ranges: [], minutes: summary.availableMinutes || 0 };
}
export const formatHours = (minutes) => {
  const rounded = Math.round(minutes);
  if (rounded > 0 && rounded < 60) return `${rounded}m`;
  return rounded % 60 ? `${Math.floor(rounded / 60)}h ${rounded % 60}m` : `${rounded / 60}h`;
};

export function summarizeDay(events, start, end) {
  const items = events.filter((event) => +new Date(event.start) < end && +new Date(event.end) > start)
    .sort((a, b) => +new Date(a.start) - +new Date(b.start));
  const intervals = (kind) => mergeIntervals(items.filter((e) => e.extendedProps.kind === kind)
    .map((e) => [Math.max(start, +new Date(e.start)), Math.min(end, +new Date(e.end))]));
  const hours = intervals("availability");
  const blocks = intervals("block");
  const appointments = items.filter((e) => e.extendedProps.kind === "appointment");
  const excluded = new Set(["cancelled", "entered-in-error"]);
  return { items, hours, workMinutes: intervalMinutes(hours), blockMinutes: intervalMinutes(blocks),
    appointmentCount: appointments.filter((e) => !excluded.has(e.extendedProps.source.status)).length,
    cancelledCount: appointments.filter((e) => e.extendedProps.source.status === "cancelled").length };
}

export function buildWeek({ date, timeZone, schedules, events, names }) {
  const start = moment.tz(date, timeZone).startOf("isoWeek");
  const days = Array.from({ length: 7 }, (_, i) => start.clone().add(i, "days"));
  const owners = new Map(schedules.map((s) => [s.scheduleId, s.practitionerRef]));
  const people = new Map(schedules.filter((s) => s.practitionerRef).map((s) => [s.practitionerRef, []]));
  events.forEach((event) => {
    const source = event.extendedProps.source;
    const refs = new Set([source.practitionerRef, ...(source.practitionerRefs || []), owners.get(source.scheduleId),
      ...(source.scheduleIds || []).map((id) => owners.get(id))].filter(Boolean));
    refs.forEach((ref) => { if (people.has(ref)) people.get(ref).push(event); });
  });
  const rows = [...people].map(([ref, items]) => ({ ref, name: names[ref] || "Loading practitioner…",
    days: days.map((day) => summarizeDay(items, +day, +day.clone().add(1, "day"))),
    summary: summarizeDay(items, +start, +start.clone().add(7, "days")),
  })).sort((a, b) => a.name.localeCompare(b.name) || a.ref.localeCompare(b.ref));
  // Multi-practitioner appointments count once in team totals, once per assigned person in rows.
  const visible = [...new Set([...people.values()].flat())];
  return { days, rows, summary: summarizeDay(visible, +start, +start.clone().add(7, "days")),
    daySummaries: days.map((day, i) => ({ ...summarizeDay(visible, +day, +day.clone().add(1, "day")),
      working: rows.filter((row) => row.days[i].workMinutes > 0).length })) };
}
